import { AccountInfo, CacheLookupPolicy, InteractionRequiredAuthError } from "@azure/msal-browser";
import { InternalAxiosRequestConfig } from "axios";
import { msalInstance } from "../auth/msalConfig";

export type AuthOptions = {
  scopes: string[];
};

type AuthInterceptor = (
  options: AuthOptions,
) => (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;

// Module-level guards: requests fire concurrently, and only ONE
// interactive flow (redirect or popup) may ever be in flight.
let redirectStarted = false;
let accountSelection: Promise<AccountInfo> | null = null;

function resolveAccount(scopes: string[]): AccountInfo | Promise<AccountInfo> {
  const active = msalInstance.getActiveAccount();
  if (active) return active;

  const all = msalInstance.getAllAccounts();

  if (all.length === 0) {
    throw new Error("No authenticated user account was found.");
  }

  if (all.length === 1) {
    msalInstance.setActiveAccount(all[0]!);
    return all[0]!;
  }

  // Multiple accounts: prompt once, share the same popup promise across
  // all concurrent requests instead of opening one popup per request.
  if (!accountSelection) {
    console.warn("Multiple accounts detected. Prompting user to select one.");
    accountSelection = msalInstance
      .loginPopup({ prompt: "select_account", scopes })
      .then((response) => {
        if (!response.account) throw new Error("Account selection failed.");
        msalInstance.setActiveAccount(response.account);
        return response.account;
      })
      .finally(() => {
        accountSelection = null;
      });
  }
  return accountSelection;
}

export const authInterceptor: AuthInterceptor =
  ({ scopes }) =>
  async (config) => {
    if (scopes.length === 0) {
      // An empty scope set causes cache misses -> iframe renewal on every
      // request. Fail loudly instead of silently degrading.
      throw new Error("authInterceptor requires at least one scope.");
    }

    const account = await resolveAccount(scopes);

    try {
      const res = await msalInstance.acquireTokenSilent({
        scopes,
        account,
        // Use cached access token or redeem the refresh token.
        // NEVER fall back to the hidden iframe (unreliable with
        // third-party cookie blocking); throw instead so we can
        // escalate to interactive login deliberately.
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });

      config.headers.set("Authorization", `Bearer ${res.accessToken}`);
      return config;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError && !redirectStarted) {
        redirectStarted = true;
        // Interactive redirect must land back on the app, NOT on the
        // config-level redirectUri (the blank silent-renewal page).
        void msalInstance.loginRedirect({
          scopes,
          redirectUri: window.location.origin,
        });
      }
      throw error;
    }
  };
